'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Eye, Edit2, Calendar, ArrowUpRight, ArrowDownRight, ArrowLeftRight } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { paymentEntries } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import {
  paymentStatusConfig,
  paymentTypeConfig,
  paymentMethodConfig,
  type PaymentStatus,
  type PaymentType,
} from '../types';
import type { PaymentEntry } from '../types';

// 결제 등록 모달
function PaymentAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="결제 등록"
      description="수금 또는 지급 내역을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="결제번호" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="결제일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="결제 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(paymentTypeConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="결제 수단" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(paymentMethodConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="거래처">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="c001">삼성전자</option>
              <option value="c002">LG전자</option>
              <option value="c003">CJ제일제당</option>
              <option value="s001">한국원지</option>
              <option value="s002">대한인쇄</option>
            </FormSelect>
          </FormField>
          <FormField label="금액" required>
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="출금/입금 계좌">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="acc001">기업은행 123-456-789</option>
              <option value="acc002">국민은행 987-654-321</option>
              <option value="acc003">신한은행 555-666-777</option>
            </FormSelect>
          </FormField>
          <FormField label="참조번호">
            <FormInput placeholder="관련 문서번호" />
          </FormField>
        </div>
        <FormField label="비고">
          <FormTextarea rows={3} placeholder="결제 관련 메모를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function PaymentListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return paymentEntries.filter((payment) => {
      // 검색 필터
      const matchesSearch =
        payment.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (payment.partyName && payment.partyName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (payment.reference && payment.reference.toLowerCase().includes(searchQuery.toLowerCase()));

      // 상태 필터
      const matchesStatus =
        selectedStatus === 'all' || payment.status === selectedStatus;

      // 유형 필터
      const matchesType =
        selectedType === 'all' || payment.paymentType === selectedType;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [searchQuery, selectedStatus, selectedType]);

  const columns = [
    {
      key: 'code',
      label: '결제번호',
      width: '12%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm text-primary whitespace-nowrap">{String(value)}</span>
      ),
    },
    {
      key: 'paymentDate',
      label: '일자',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm whitespace-nowrap">
          <Calendar className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span>{String(value)}</span>
        </div>
      ),
    },
    {
      key: 'paymentType',
      label: '유형',
      width: '10%',
      render: (value: unknown) => {
        const type = value as PaymentType;
        const config = paymentTypeConfig[type];
        const colorClasses: Record<string, string> = {
          blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
          purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        };
        const icons: Record<PaymentType, React.ReactNode> = {
          receive: <ArrowUpRight className="w-3 h-3 flex-shrink-0" />,
          pay: <ArrowDownRight className="w-3 h-3 flex-shrink-0" />,
          internal_transfer: <ArrowLeftRight className="w-3 h-3 flex-shrink-0" />,
        };
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {icons[type]}
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'partyName',
      label: '거래처',
      width: '18%',
      render: (value: unknown, row: PaymentEntry) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{value ? String(value) : '-'}</div>
          {row.reference && (
            <div className="text-xs text-muted-foreground truncate">
              참조: {row.reference}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'paymentMethod',
      label: '결제수단',
      width: '10%',
      render: (value: unknown) => {
        const method = value as keyof typeof paymentMethodConfig;
        return <span className="text-sm whitespace-nowrap">{paymentMethodConfig[method].label}</span>;
      },
    },
    {
      key: 'amount',
      label: '금액',
      width: '14%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown, row: PaymentEntry) => {
        const isReceive = row.paymentType === 'receive';
        return (
          <span className={`font-mono text-sm font-medium whitespace-nowrap ${isReceive ? 'text-green-500' : 'text-red-500'}`}>
            {isReceive ? '+' : '-'}{formatCurrency(value as number)}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: '상태',
      width: '8%',
      render: (value: unknown) => {
        const status = value as PaymentStatus;
        const config = paymentStatusConfig[status];
        const colorClasses: Record<string, string> = {
          gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
          green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${colorClasses[config.color]}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="보기">
            <Eye className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 유형별 통계
  const typeStats = useMemo(() => {
    const stats: Record<PaymentType, { count: number; amount: number }> = {
      receive: { count: 0, amount: 0 },
      pay: { count: 0, amount: 0 },
      internal_transfer: { count: 0, amount: 0 },
    };

    paymentEntries.forEach((payment) => {
      if (payment.status !== 'cancelled') {
        stats[payment.paymentType].count++;
        stats[payment.paymentType].amount += payment.amount;
      }
    });

    return stats;
  }, []);

  // 요약 정보
  const totalReceived = typeStats.receive.amount;
  const totalPaid = typeStats.pay.amount;

  return (
    <div className="space-y-6">
      <PageHeader
        title="결제 관리"
        description="수금 및 지급 내역을 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            결제 등록
          </button>
        }
      />

      {/* 유형별 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(Object.entries(paymentTypeConfig) as [PaymentType, { label: string; color: string }][]).map(
          ([type, config]) => {
            const stat = typeStats[type];
            const colorClasses: Record<string, string> = {
              blue: 'border-blue-200 dark:border-blue-800',
              orange: 'border-orange-200 dark:border-orange-800',
              purple: 'border-purple-200 dark:border-purple-800',
            };
            const icons: Record<PaymentType, React.ReactNode> = {
              receive: <ArrowUpRight className="w-5 h-5 text-blue-500" />,
              pay: <ArrowDownRight className="w-5 h-5 text-orange-500" />,
              internal_transfer: <ArrowLeftRight className="w-5 h-5 text-purple-500" />,
            };
            return (
              <button
                key={type}
                onClick={() => setSelectedType(selectedType === type ? 'all' : type)}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  selectedType === type
                    ? 'border-primary bg-primary/5'
                    : `${colorClasses[config.color]} bg-card hover:bg-accent/50`
                }`}
              >
                <div className="flex items-center gap-2">
                  {icons[type]}
                  <span className="text-sm text-muted-foreground">{config.label}</span>
                </div>
                <div className="text-2xl font-bold mt-2">{stat.count}건</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {formatCurrency(stat.amount)}
                </div>
              </button>
            );
          }
        )}
      </div>

      {/* 요약 정보 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 수금액</div>
          <div className="text-2xl font-bold mt-1 text-green-500">
            +{formatCurrency(totalReceived)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 지급액</div>
          <div className="text-2xl font-bold mt-1 text-red-500">
            -{formatCurrency(totalPaid)}
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="결제번호, 거래처, 참조 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 상태 필터 */}
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(paymentStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        {/* 유형 필터 */}
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 유형</option>
          {Object.entries(paymentTypeConfig).map(([type, config]) => (
            <option key={type} value={type}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 결제 등록 모달 */}
      <PaymentAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
